import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  CheckCircle, 
  CreditCard,
  Smartphone,
  Bell,
  QrCode,
  DollarSign,
  MessageSquare,
  Star,
  Users,
  Utensils,
  Shield,
  Eye,
  Clock,
  Download,
  Receipt
} from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';

const ParentsOnboarding = ({ isOpen, onClose, onComplete, parentName }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const steps = [
    {
      id: 'welcome',
      title: '👋 Bem-vindo ao Portal dos Pais!',
      subtitle: `Olá ${parentName || 'Responsável'}! Vamos te ajudar a conhecer todas as funcionalidades`,
      icon: Users,
      content: (
        <div className="space-y-4">
          <div className="text-center">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
              <Utensils className="w-12 h-12 text-white" />
            </div>
            <p className="text-gray-600">
              Com o Portal dos Pais você pode acompanhar os gastos dos seus filhos na cantina, 
              fazer recargas, receber notificações e muito mais!
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">🎯 O que você vai aprender:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Como consultar o saldo dos seus filhos</li>
              <li>• Como fazer recargas via PIX</li>
              <li>• Como acompanhar o histórico de compras</li>
              <li>• Como usar o app mobile</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'balance',
      title: '💰 Consultando o Saldo',
      subtitle: 'Acompanhe o saldo dos seus filhos em tempo real',
      icon: DollarSign,
      content: (
        <div className="space-y-4">
          <div className="bg-white border-2 border-dashed border-blue-300 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">João Silva</h3>
                  <p className="text-sm text-gray-500">6º Ano - Turma A</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">R$ 25,50</p>
                <p className="text-xs text-gray-500">Saldo atual</p>
              </div>
            </div>
            <div className="flex items-center text-green-600 text-sm">
              <CheckCircle className="w-4 h-4 mr-1" />
              Atualizado agora
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">📊 Funcionalidades:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• ✅ Saldo atualizado em tempo real</li>
              <li>• 🔄 Sincronização automática</li>
              <li>• 📱 Disponível no app mobile</li>
              <li>• 🔔 Alertas de saldo baixo</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'recharge',
      title: '📱 Fazendo Recargas via PIX',
      subtitle: 'Recarregue o saldo de forma rápida e segura',
      icon: QrCode,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <QrCode className="w-8 h-8 text-purple-600 mb-2" />
              <h4 className="font-semibold text-purple-900">PIX QR Code</h4>
              <p className="text-sm text-purple-700">Escaneie e pague pelo app do seu banco</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <Smartphone className="w-8 h-8 text-green-600 mb-2" />
              <h4 className="font-semibold text-green-900">PIX Copia e Cola</h4>
              <p className="text-sm text-green-700">Copie o código e cole no seu app</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-amber-600 mr-2" />
              <div>
                <h4 className="font-semibold text-amber-900">Processamento Automático</h4>
                <p className="text-sm text-amber-700">
                  O saldo é creditado automaticamente em até 5 minutos após o pagamento
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'history',
      title: '📋 Histórico de Compras',
      subtitle: 'Acompanhe tudo que foi comprado na cantina',
      icon: Receipt,
      content: (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg">
            <div className="border-b p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Compra na Cantina</h4>
                  <p className="text-sm text-gray-500">Hoje, 14:30</p>
                </div>
                <span className="text-red-600 font-semibold">-R$ 8,50</span>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>1x Sanduíche Natural</span>
                <span>R$ 5,00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>1x Suco de Laranja</span>
                <span>R$ 3,50</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">📈 Relatórios disponíveis:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 📅 Gastos por período</li>
              <li>• 🍎 Produtos mais comprados</li>
              <li>• 💸 Médias de gastos diários/semanais</li>
              <li>• 📊 Gráficos de evolução</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'notifications',
      title: '🔔 Notificações e Alertas',
      subtitle: 'Fique sempre informado sobre as atividades dos seus filhos',
      icon: Bell,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <div className="flex items-center">
                <Bell className="w-5 h-5 text-blue-600 mr-2" />
                <div>
                  <h4 className="font-semibold text-blue-900">Compra Realizada</h4>
                  <p className="text-sm text-blue-700">João comprou um lanche - R$ 8,50</p>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-amber-600 mr-2" />
                <div>
                  <h4 className="font-semibold text-amber-900">Saldo Baixo</h4>
                  <p className="text-sm text-amber-700">Saldo abaixo de R$ 10,00 - Faça uma recarga</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <h4 className="font-semibold text-green-900">Recarga Confirmada</h4>
                  <p className="text-sm text-green-700">PIX de R$ 20,00 processado com sucesso</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'mobile-app',
      title: '📱 App Mobile CantinaTech',
      subtitle: 'Baixe o app e tenha tudo na palma da sua mão',
      icon: Smartphone,
      content: (
        <div className="space-y-4">
          <div className="text-center">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
              <Smartphone className="w-10 h-10 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Disponível na App Store e Play Store</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-black text-white rounded-lg p-4 flex items-center space-x-3">
              <div className="bg-white rounded p-1">
                <Download className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="text-xs">Baixar na</p>
                <p className="font-semibold">App Store</p>
              </div>
            </div>
            <div className="bg-green-600 text-white rounded-lg p-4 flex items-center space-x-3">
              <div className="bg-white rounded p-1">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs">Baixar no</p>
                <p className="font-semibold">Google Play</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">🚀 Funcionalidades do app:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• 🔔 Notificações push em tempo real</li>
              <li>• 💰 Consulta de saldo offline</li>
              <li>• 📱 Recargas via PIX mais rápidas</li>
              <li>• 📊 Relatórios detalhados</li>
              <li>• 🔒 Login com biometria</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: '🔒 Segurança e Privacidade',
      subtitle: 'Seus dados e dos seus filhos estão sempre protegidos',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <Shield className="w-8 h-8 text-green-600 mb-2" />
              <h4 className="font-semibold text-green-900">Criptografia</h4>
              <p className="text-sm text-green-700">Todos os dados são criptografados</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <Eye className="w-8 h-8 text-blue-600 mb-2" />
              <h4 className="font-semibold text-blue-900">Privacidade</h4>
              <p className="text-sm text-blue-700">Você só vê dados dos seus filhos</p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">🛡️ Medidas de segurança:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• ✅ Autenticação segura por telefone</li>
              <li>• ✅ Conexão SSL/TLS em todas as operações</li>
              <li>• ✅ Backup automático dos dados</li>
              <li>• ✅ Conformidade com LGPD</li>
              <li>• ✅ Auditoria de todas as transações</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'complete',
      title: '🎉 Tudo Pronto!',
      subtitle: 'Você já conhece todas as funcionalidades do Portal dos Pais',
      icon: CheckCircle,
      content: (
        <div className="space-y-4 text-center">
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-gray-900">Parabéns! 🎊</h3>
            <p className="text-gray-600">
              Agora você já sabe como usar todas as funcionalidades do Portal dos Pais.
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">🎯 Próximos passos:</h4>
            <ul className="text-sm text-blue-800 space-y-1 text-left">
              <li>• Explore o saldo dos seus filhos</li>
              <li>• Faça sua primeira recarga via PIX</li>
              <li>• Baixe o app mobile</li>
              <li>• Configure as notificações</li>
            </ul>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-center text-green-700">
              <MessageSquare className="w-5 h-5 mr-2" />
              <span className="font-semibold">Precisa de ajuda? Use o chat de suporte!</span>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    
    // Salvar que o onboarding foi completado
    localStorage.setItem('parentsOnboardingCompleted', 'true');
    localStorage.setItem('parentsOnboardingDate', new Date().toISOString());
    
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('parentsOnboardingSkipped', 'true');
    localStorage.setItem('parentsOnboardingDate', new Date().toISOString());
    onClose();
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{currentStepData.title}</h2>
                <p className="text-blue-100 text-sm">{currentStepData.subtitle}</p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-blue-100 mt-2">
            Passo {currentStep + 1} de {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep
                      ? 'bg-blue-500'
                      : completedSteps.has(index)
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex items-center space-x-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="flex items-center"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={nextStep}
                  className="flex items-center"
                >
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleComplete}
                  className="flex items-center bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Finalizar
                </Button>
              )}
            </div>
          </div>
          
          {currentStep === 0 && (
            <div className="mt-4 text-center">
              <button
                onClick={handleSkip}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Pular apresentação
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentsOnboarding;
